import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useRaces } from '@/hooks/useRaces';
import { useOrganizers } from '@/hooks/useOrganizers';
import { usePublicSeries } from '@/hooks/useRaceSeries';
import { useRegistrations } from '@/hooks/useRegistrations';
import { raceTypesAPI } from '@/services/raceTypesApi';
import { transformRaceToEvent } from '@/utils/transforms';
import { EventItem } from './eventsmanager.props';
import { useEventsActions } from './useEventsActions';
import EventsManagerView from './eventsmanager.view';

const EventsManager = () => {
  const { user, isOrganizer } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const actions = useEventsActions(isOrganizer, user);

  const raceParams = isOrganizer() && user?.organizerId ? { organizerId: user.organizerId } : {};
  const { data: racesRaw = [], isLoading: loading } = useRaces(raceParams);
  const { data: organizers = [] } = useOrganizers();
  const { data: allSeries = [] } = usePublicSeries();

  const events = useMemo(() => racesRaw.map(transformRaceToEvent), [racesRaw]);

  // Fetch race types per event
  const raceIds = useMemo(() => racesRaw.map((r: any) => r.id), [racesRaw]);
  const { data: raceTypesByEvent = {} } = useQuery({
    queryKey: ['raceTypesByEvent', raceIds],
    queryFn: async () => {
      const raceTypes: Record<string, Array<{ id: number; name: string }>> = {};
      await Promise.all(
        raceIds.map(async (id: string) => {
          try {
            const result = await raceTypesAPI.getRaceTypes(id);
            raceTypes[id] = result.map((rt: any) => ({ id: rt.id, name: rt.name }));
          } catch {
            raceTypes[id] = [];
          }
        })
      );
      return raceTypes;
    },
    enabled: raceIds.length > 0,
  });

  // Count registrations per event from the all-registrations endpoint
  const { data: allRegistrations = [] } = useRegistrations();
  const eventCounts = useMemo(() => {
    const createTshirtGroup = () => ({ total: 0, sizes: {} as Record<string, number> });
    const createEventCounters = (raceTypesCount = 0) => ({
      raceTypes: raceTypesCount,
      registrations: 0,
      registrationByRaceType: [] as Array<{
        raceTypeId: string;
        raceTypeName: string;
        total: number;
        paymentStatuses: Record<string, number>;
      }>,
      tshirtSizes: {
        male: createTshirtGroup(),
        female: createTshirtGroup(),
        kids: createTshirtGroup(),
        unknown: createTshirtGroup(),
        total: 0,
      },
    });

    const classifyTshirtGroup = (size: string, gender?: string) => {
      const normalizedSize = size.toLowerCase();
      if (/(kid|child|junior|дет|деца|јуниор)/.test(normalizedSize)) return 'kids';
      if (/\b(female|women|woman|lady)\b|жена|женски|девој/.test(normalizedSize)) return 'female';
      if (/\b(male|men|man)\b|машки|мажи/.test(normalizedSize)) return 'male';

      const normalizedGender = (gender || '').toLowerCase();
      if (/^(female|woman|women|f|ж|женско)$/.test(normalizedGender)) return 'female';
      if (/^(male|man|men|m|м|машко)$/.test(normalizedGender)) return 'male';
      return 'unknown';
    };

    const raceTypeNameMaps: Record<string, Record<string, string>> = {};
    Object.entries(raceTypesByEvent).forEach(([raceId, raceTypes]) => {
      raceTypeNameMaps[raceId] = {};
      raceTypes.forEach((rt: any) => {
        raceTypeNameMaps[raceId][String(rt.id)] = rt.name;
      });
    });

    const counts: Record<string, ReturnType<typeof createEventCounters>> = {};
    const raceTypeTotals: Record<string, Record<string, { total: number; paymentStatuses: Record<string, number> }>> = {};

    raceIds.forEach((raceId: string) => {
      counts[raceId] = createEventCounters((raceTypesByEvent[raceId] || []).length);
      raceTypeTotals[raceId] = {};
    });

    allRegistrations.forEach((reg: any) => {
      if (!reg.raceId) return;
      const raceId = String(reg.raceId);

      if (!counts[raceId]) {
        counts[raceId] = createEventCounters((raceTypesByEvent[raceId] || []).length);
      }
      if (!raceTypeTotals[raceId]) raceTypeTotals[raceId] = {};

      counts[raceId].registrations += 1;

      if (reg.raceTypeId != null) {
        const raceTypeId = String(reg.raceTypeId);
        if (!raceTypeTotals[raceId][raceTypeId]) {
          raceTypeTotals[raceId][raceTypeId] = { total: 0, paymentStatuses: {} };
        }
        const raceTypeSummary = raceTypeTotals[raceId][raceTypeId];
        raceTypeSummary.total += 1;
        const paymentStatus = reg.paymentStatus || 'Unknown';
        raceTypeSummary.paymentStatuses[paymentStatus] = (raceTypeSummary.paymentStatuses[paymentStatus] || 0) + 1;
      }

      const tshirtSize = (reg.tshirtSize || '').trim();
      if (tshirtSize) {
        const group = classifyTshirtGroup(tshirtSize, reg.gender) as 'male' | 'female' | 'kids' | 'unknown';
        const groupSummary = counts[raceId].tshirtSizes[group];
        groupSummary.total += 1;
        groupSummary.sizes[tshirtSize] = (groupSummary.sizes[tshirtSize] || 0) + 1;
        counts[raceId].tshirtSizes.total += 1;
      }
    });

    Object.keys(counts).forEach((raceId) => {
      counts[raceId].registrationByRaceType = Object.entries(raceTypeTotals[raceId] || {})
        .map(([raceTypeId, summary]) => ({
          raceTypeId,
          raceTypeName: raceTypeNameMaps[raceId]?.[raceTypeId] || `Тип ${raceTypeId}`,
          total: summary.total,
          paymentStatuses: summary.paymentStatuses,
        }))
        .sort((a, b) => b.total - a.total);
    });

    return counts;
  }, [allRegistrations, raceIds, raceTypesByEvent]);

  const getEventCounts = useCallback((eventId: string) => (
    eventCounts[eventId] || {
      raceTypes: 0,
      registrations: 0,
      registrationByRaceType: [],
      tshirtSizes: {
        male: { total: 0, sizes: {} },
        female: { total: 0, sizes: {} },
        kids: { total: 0, sizes: {} },
        unknown: { total: 0, sizes: {} },
        total: 0,
      },
    }
  ), [eventCounts]);

  const filteredEvents = events.filter(
    (event: any) =>
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isRegistrationOpen = (event: EventItem) => {
    if (!event.openRegistrationDate || !event.closeRegistrationDate) return null;
    const now = new Date();
    const openDate = new Date(event.openRegistrationDate);
    const closeDate = new Date(event.closeRegistrationDate);
    if (now < openDate) return 'upcoming';
    if (now > closeDate) return 'closed';
    return 'open';
  };

  const getRegStatusVariant = (status: string) => {
    if (status === 'open') return 'info';
    if (status === 'upcoming') return 'warning';
    return 'error';
  };
  const getRegStatusLabel = (status: string) => {
    if (status === 'open') return 'Регистрација: Отворена';
    if (status === 'upcoming') return 'Регистрација: Наскоро';
    return 'Регистрација: Затворена';
  };

  return (
    <EventsManagerView
      loading={loading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filteredEvents={filteredEvents}
      isDialogOpen={actions.isDialogOpen}
      setIsDialogOpen={actions.setIsDialogOpen}
      editingEvent={actions.editingEvent}
      setEditingEvent={actions.setEditingEvent}
      formData={actions.formData}
      setFormData={actions.setFormData}
      handleSubmit={actions.handleSubmit}
      handleEdit={actions.handleEdit}
      handleDelete={actions.handleDelete}
      deleteTarget={actions.deleteTarget}
      setDeleteTarget={actions.setDeleteTarget}
      confirmDelete={actions.confirmDelete}
      organizers={organizers}
      allSeries={allSeries}
      isOrganizer={isOrganizer}
      user={user}
      selectedEvent={selectedEvent}
      setSelectedEvent={setSelectedEvent}
      isRegistrationOpen={isRegistrationOpen}
      getRegStatusVariant={getRegStatusVariant}
      getRegStatusLabel={getRegStatusLabel}
      getEventCounts={getEventCounts}
    />
  );
};

export default EventsManager;
